/*
/*
 This file is part of GNU Taler
 (C) 2022 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  InternationalizationAPI,
  OperationFail,
  TalerError,
  TranslatedString,
} from "@gnu-taler/taler-util";
import { VNode, h } from "preact";
import { useCommonPreferences } from "../context/common-preferences.js";
import { useTranslationContext } from "../context/translation.js";
import { translateTalerError } from "../hooks/useNotifications.js";
import { Attention } from "./Attention.js";

export function ErrorLoading({
  title,
  error,
}: {
  title: TranslatedString;
  error: TalerError;
}): VNode {
  const { i18n } = useTranslationContext();
  const description = translateTalerError(error, i18n);
  const [{ showDebugInfo }] = useCommonPreferences();
  const debug = showDebugInfo
    ? JSON.stringify(
        error.errorDetail,
        function excludePrivate(key, value) {
          if (key.startsWith("__")) return "...";
          return value;
        },
        2,
      )
    : undefined;
  return (
    <Attention
      type="danger"
      copy
      copyText={[title, ...(description ?? []), debug]
        .filter((value): value is string => !!value)
        .join("\n\n")}
      title={title}
    >
      {description.length || debug ? (
        <details>
          <summary class="min-h-11 cursor-pointer py-2 font-semibold underline">
            <i18n.Translate>Technical details</i18n.Translate>
          </summary>
          <div class="space-y-1">
            {description.map((d, idx) => (
              <p key={idx}>{d}</p>
            ))}
            {debug ? (
              <pre class="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md bg-white/70 p-3 text-xs text-black">
                {debug}
              </pre>
            ) : undefined}
          </div>
        </details>
      ) : undefined}
    </Attention>
  );
}

export function FailLoading<T>({
  title,
  translate,
  operation,
}: {
  title: TranslatedString;
  operation: OperationFail<T>;
  translate: (f: OperationFail<T>, i18n: InternationalizationAPI) => VNode;
}): VNode {
  const { i18n } = useTranslationContext();
  const [{ showDebugInfo }] = useCommonPreferences();
  const debug = showDebugInfo
    ? JSON.stringify(
        operation,
        function excludePrivate(key, value) {
          if (key.startsWith("__")) return "...";
          return value;
        },
        2,
      )
    : undefined;
  return (
    <Attention type="danger" copy copyText={debug ?? title} title={title}>
      {translate(operation, i18n)}
      {debug ? (
        <pre class="whitespace-break-spaces text-black">{debug}</pre>
      ) : undefined}
    </Attention>
  );
}
